import { ColorPalette } from '@main/theme';
import {
  Content,
  Header,
  Item,
  Root,
  Trigger,
} from '@radix-ui/react-accordion';
import { darken, transparentize } from 'polished';
import styled from 'styled-components';

/**
 * The "log level" indicating color
 */
export type CalloutLevel = 'info' | 'success' | 'warning' | 'error';

export const levelColors: Record<CalloutLevel, keyof ColorPalette> = {
  info: 'blue1',
  success: 'success',
  warning: 'warning',
  error: 'danger',
};

export const CalloutBackground = styled.div<{
  /** The "log level" indicating color */
  level: CalloutLevel;
}>`
  background-color: ${({ theme, level }) =>
    transparentize(0.75, theme.colors[levelColors[level]])};
  border: 1px solid
    ${({ theme, level }) =>
      transparentize(0.9, theme.colors[levelColors[level]])};
  border-radius: 0.5em;

  & p,
  div {
    color: ${({ theme, level }) =>
      darken(0.4, theme.colors[levelColors[level]])};
  }
`;

export const StyledAccordionRoot = styled(Root)`
  @keyframes slideDown {
    from {
      height: 0;
    }
    to {
      height: var(--radix-accordion-content-height);
    }
  }

  @keyframes slideUp {
    from {
      height: var(--radix-accordion-content-height);
    }
    to {
      height: 0;
    }
  }
`;

export const StyledAccordionItem = styled(Item)`
  overflow: hidden;

  padding: 0.2em 0;
  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

export const StyledAccordionHeader = styled(Header)`
  all: unset;
`;

export const StyledAccordionTrigger = styled(Trigger)`
  all: unset;

  background-color: transparent;
  border: none;
  border-radius: 0.5em;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.3em;

  & > svg {
    transition: transform 100ms;
  }

  &[data-state='open'] > svg {
    transform: rotate(90deg);
  }
`;

export const StyledAccordionContent = styled(Content)`
  background-color: white;
  border-radius: 0.5em;
  margin-top: 0.2em;

  &[data-state='open'] {
    animation: slideDown 150ms ease-in-out;
  }
  &[data-state='closed'] {
    animation: slideUp 150ms ease-in-out;
  }
`;

export const CodeBlockWrapper = styled.div`
  padding: 0.5em 0.75em;
  max-height: 300px;
  overflow-y: auto;
`;
